/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { TagEntry } from '../model/tagEntry';
import { TagPaging } from '../model/tagPaging';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class TagsApi extends BaseApi {
    createTagForNode(nodeId, tagBodyCreate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(tagBodyCreate, 'tagBodyCreate');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/tags',
            pathParams,
            queryParams,
            bodyParam: tagBodyCreate,
            returnType: TagEntry
        });
    }
    deleteTagFromNode(nodeId, tagId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(tagId, 'tagId');
        const pathParams = {
            nodeId,
            tagId
        };
        return this.delete({
            path: '/nodes/{nodeId}/tags/{tagId}',
            pathParams
        });
    }
    getTag(tagId, opts) {
        throwIfNotDefined(tagId, 'tagId');
        const pathParams = {
            tagId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/tags/{tagId}',
            pathParams,
            queryParams,
            returnType: TagEntry
        });
    }
    listTags(opts) {
        opts = opts || {};
        const pathParams = {};
        let where;
        if (opts === null || opts === void 0 ? void 0 : opts.tag) {
            where = opts.matching ? `(tag matches ('${opts.tag}'))` : `(tag='${opts.tag}')`;
        }
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            where
        };
        return this.get({
            path: '/tags',
            pathParams,
            queryParams,
            returnType: TagPaging
        });
    }
    listTagsForNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/tags',
            pathParams,
            queryParams,
            returnType: TagPaging
        });
    }
    updateTag(tagId, tagBodyUpdate, opts) {
        throwIfNotDefined(tagId, 'tagId');
        throwIfNotDefined(tagBodyUpdate, 'tagBodyUpdate');
        const pathParams = {
            tagId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/tags/{tagId}',
            pathParams,
            queryParams,
            bodyParam: tagBodyUpdate,
            returnType: TagEntry
        });
    }
    deleteTag(tagId) {
        throwIfNotDefined(tagId, 'tagId');
        const pathParams = {
            tagId
        };
        return this.delete({
            path: '/tags/{tagId}',
            pathParams
        });
    }
    createTags(tags) {
        throwIfNotDefined(tags, 'tags');
        return this.post({
            path: '/tags',
            bodyParam: tags
        });
    }
    assignTagsToNode(nodeId, tags) {
        return this.post({
            path: '/nodes/{nodeId}/tags',
            pathParams: { nodeId },
            bodyParam: tags,
            returnType: TagPaging
        });
    }
    assignTagToNode(nodeId, tag) {
        return this.post({
            path: '/nodes/{nodeId}/tags',
            pathParams: { nodeId },
            bodyParam: tag,
            returnType: TagEntry
        });
    }
}
